/**
 * The game page, `/contests/:id` (docs/contest-ux.md, incl. Revision 1). A slim one-row hero
 * (teams + logos or the event name, league · venues, start time or live clock + score, one
 * signals / best-edge chip, freshness dot), then category pills (Main · Spreads · Totals ·
 * Player props · Other) and a search box, then the category's markets. Main on a head-to-head
 * game is the Game lines table (Moneyline · Spread · Total) plus Popular props; elsewhere every
 * market is a soft card with best prices. Tapping a price opens the shared ticket drawer (push
 * drawer on desktop, bottom sheet on mobile).
 *
 * ≥1280px: markets on the left (~2/3) and a sticky insight column on the right for the last
 * tapped / hovered market (every venue's price, Book, Chart). The ticket drawer replaces it
 * while open. The selection is page state only (not in the URL).
 *
 * Data: the FULL contest liquidity (stream-patched `useLiquidity`); no board route here.
 * Category, stat and search live in the URL so Back keeps them. `?market=<market_key>` (and
 * `?pos=<position_hash>`) select the category, scroll to that card and highlight it briefly.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { LiquidityPosition, PartnerLiquidity } from "@greenbook/client";
import {
  useAccountPartners,
  useContest,
  useContestMarkets,
  useContestState,
  useLeagues,
  useLiquidity,
  useSignals,
} from "../lib/queries.js";
import { scanBestEdge, sideColumnLabel, type PositionLike } from "../lib/books.js";
import { quotableMarkets } from "../lib/marketMaking.js";
import { marketHref } from "../lib/marketView.js";
import { isGame, teamsOf } from "../lib/marketsBoard.js";
import {
  buildContestModel,
  CATEGORIES,
  categoryLabel,
  defaultCategory,
  findTarget,
  gameLines,
  isCategory,
  moneylineSlice,
  popularProps,
  sliceOf,
  searchCards,
  withStat,
  type Category,
  type DeepTarget,
  type MarketCard,
} from "../lib/contestMarkets.js";
import { useTradingMode } from "../lib/venues.js";
import { useIsAuthed } from "../lib/omauth.js";
import { useIsMobile, useIsWide } from "../lib/responsive.js";
import { useLiveContest, useLiquidityAge, useRelayStatus } from "../lib/ws.js";
import { Pill, SectionHeading, TicketDrawer } from "../components/kit.js";
import { AccessErrorCard } from "../components/Gate.js";
import { TradeTicket } from "../components/TradeTicket.js";
import { DepthBook } from "../components/DepthBook.js";
import { CompactHero, GameHero, HeroSkeleton, heroState, type HeroChip } from "../components/contest/GameHero.js";
import {
  CardSkeleton,
  MarketCardView,
  type CardCtx,
  type CardFocus,
  type InsightSel,
} from "../components/contest/MarketCards.js";
import { GameLinesCard, PopularPropsCard } from "../components/contest/GameLines.js";
import { InsightColumn } from "../components/contest/InsightColumn.js";

interface TicketPick {
  position: LiquidityPosition;
  pl: PartnerLiquidity;
}

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** How long a deep-linked card keeps its highlight ring. */
const HIGHLIGHT_MS = 1600;
/** Hover must rest this long before the insight column follows it (no flicker on mouse-through). */
const HOVER_MS = 350;
/** The markets grid: as many ≥24rem columns as fit, stretched to fill (no empty right third). */
const CARD_GRID = "grid grid-cols-[repeat(auto-fit,minmax(min(100%,24rem),1fr))] items-start gap-3";

export function ContestDetail() {
  const { id } = useParams<{ id: string }>();
  const contestId = id ? decodeURIComponent(id) : undefined;
  const [params, setParams] = useSearchParams();

  const contestQ = useContest(contestId);
  const stateQ = useContestState(contestId);
  const liquidityQ = useLiquidity(contestId);
  const marketsQ = useContestMarkets(contestId);
  const leaguesQ = useLeagues();
  const signalsQ = useSignals();
  useLiveContest(contestId);

  const isMobile = useIsMobile();
  const isWide = useIsWide();
  const authed = useIsAuthed();
  const mode = useTradingMode();

  /* ------------------------------------------------------------- venues */

  // Real mode: prices come from the connected venues (Markets' rule: if none are connected, every
  // venue). The "+N" list always shows every venue and marks the connected ones.
  const partnersQ = useAccountPartners();
  const realVenues = useMemo(() => {
    if (mode !== "real" || !authed) return null;
    const s = new Set<string>();
    for (const ap of partnersQ.data?.account_partners ?? []) {
      if (ap.status === "active" && ap.credential_status === "connected") s.add(ap.partner_id);
    }
    return s;
  }, [mode, authed, partnersQ.data]);
  const allowed = realVenues && realVenues.size > 0 ? realVenues : null;

  /* -------------------------------------------------------------- model */

  const contest = contestQ.data;
  const positions = useMemo(() => liquidityQ.data ?? [], [liquidityQ.data]);
  const model = useMemo(
    () =>
      contest
        ? buildContestModel(positions, marketsQ.data ?? [], contest)
        : null,
    [positions, marketsQ.data, contest],
  );

  const warned = useRef(new Set<string>());
  useEffect(() => {
    for (const k of model?.unjoinedKeys ?? []) {
      if (warned.current.has(k)) continue;
      warned.current.add(k);
      // eslint-disable-next-line no-console
      console.warn(`[greenbook] market_key "${k}" has no market definition on this contest — treating as moneyline-like.`);
    }
  }, [model?.unjoinedKeys]);

  const teams = contest ? teamsOf(contest) : null;
  const logos = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of positions) {
      const url = (p as { participant_image_url?: string | null }).participant_image_url;
      const name = (p.participant_name ?? "").toLowerCase();
      if (url && name && !m.has(name)) m.set(name, url);
    }
    return m;
  }, [positions]);
  const league = useMemo(
    () => (leaguesQ.data ?? []).find((l) => l.id === contest?.league_id),
    [leaguesQ.data, contest?.league_id],
  );
  const hero = heroState(stateQ.data);

  /* ---------------------------------------------------------- URL state */

  const setParam = useCallback(
    (patch: Record<string, string | null>) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          for (const [k, v] of Object.entries(patch)) {
            if (v) next.set(k, v);
            else next.delete(k);
          }
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  const catParam = params.get("cat");
  const stat = params.get("stat");
  const category: Category = isCategory(catParam) ? catParam : model ? defaultCategory(model.byCategory) : "main";
  const selectCategory = (c: Category) => setParam({ cat: c, stat: c === "props" ? stat : null });

  // Search: typed instantly, mirrored into ?q= after a short pause.
  const [search, setSearch] = useState(() => params.get("q") ?? "");
  useEffect(() => {
    const t = setTimeout(() => setParam({ q: search.trim() || null }), 250);
    return () => clearTimeout(t);
  }, [search, setParam]);
  const needle = search.trim();

  /* ------------------------------------------------------------ ticket */

  const [ticket, setTicket] = useState<TicketPick | null>(null);
  const closeTicket = useCallback(() => setTicket(null), []);
  const onPick = useCallback((position: LiquidityPosition, pl: PartnerLiquidity) => setTicket({ position, pl }), []);

  /* ----------------------------------------------------- insight column */

  // The market the insight column shows (null = the default: the moneyline / first market).
  const [insight, setInsight] = useState<InsightSel | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const inspect = useCallback((sel: InsightSel) => {
    clearTimeout(hoverTimer.current);
    setInsight(sel);
  }, []);
  const hover = useCallback((sel: InsightSel | null) => {
    clearTimeout(hoverTimer.current);
    if (sel) hoverTimer.current = setTimeout(() => setInsight(sel), HOVER_MS);
  }, []);
  useEffect(() => () => clearTimeout(hoverTimer.current), []);
  // A new contest starts on its default market again.
  useEffect(() => setInsight(null), [contestId]);

  const gl = useMemo(() => (model && contest ? gameLines(model, contest) : null), [model, contest]);
  const popular = useMemo(() => (model ? popularProps(model) : []), [model]);

  const posByHash = useMemo(() => {
    const m = new Map<string, LiquidityPosition>();
    for (const p of positions) m.set(p.position_hash, p);
    return m;
  }, [positions]);
  /** The default market: the game's moneyline (all its sides), else the first main market. */
  const defaultInsight: InsightSel | null = useMemo(() => {
    if (!model) return null;
    if (gl) {
      const slice = moneylineSlice(gl);
      return slice.length ? { title: gl.moneyline.title, hashes: slice.map((p) => p.position_hash), focus: slice[0].position_hash } : null;
    }
    const card = model.byCategory[defaultCategory(model.byCategory)][0];
    if (!card) return null;
    const slice =
      card.kind === "rows" && card.rowKind === "outcomes" && card.rows.length > 3
        ? (card.rows[0].rungs[card.rows[0].mainIndex]?.sides ?? [])
        : sliceOf(card, card.kind === "ladder" ? card.mainIndex : 0, null, (r) => r.mainIndex);
    if (!slice.length) return null;
    const title = card.kind === "rows" && card.rowKind === "outcomes" && card.rows.length > 3 ? `${card.title} · ${card.rows[0].label}` : card.title;
    return { title, hashes: slice.map((p) => p.position_hash), focus: slice[0].position_hash };
  }, [model, gl]);
  // Resolve against the live positions every render so stream pushes update the column.
  const shown = useMemo(() => {
    for (const sel of [insight, defaultInsight]) {
      if (!sel) continue;
      const sides = sel.hashes.map((h) => posByHash.get(h)).filter((p): p is LiquidityPosition => !!p);
      if (sides.length) return { ...sel, sides };
    }
    return null;
  }, [insight, defaultInsight, posByHash]);

  // Market names and lines link to their Market page when it can show them (a two-sided book).
  // Read through a ref so a stream push doesn't hand every card a new ctx.
  const quotable = useMemo(() => {
    const s = new Set<string>();
    for (const m of quotableMarkets(positions, marketsQ.data ?? [])) for (const b of m.books) for (const p of b.positions) s.add(p.position_hash);
    return s;
  }, [positions, marketsQ.data]);
  const quotableRef = useRef(quotable);
  quotableRef.current = quotable;
  const marketLink = useCallback(
    (slice: PositionLike[]) => {
      const p = slice.find((x) => quotableRef.current.has(x.position_hash));
      return p && contestId ? marketHref({ contestId, positionHash: p.position_hash }) : null;
    },
    [contestId],
  );

  const ctx: CardCtx = useMemo(
    () => ({
      contestId: contestId ?? "",
      allowed,
      realVenues,
      active: ticket ? { positionHash: ticket.position.position_hash, partnerId: ticket.pl.partner_id } : null,
      teams,
      onPick,
      wide: isWide,
      inspect,
      hover,
      inspectedHash: isWide ? (shown?.focus ?? null) : null,
      marketLink,
    }),
    // `teams` is derived from `contest`; keying on it avoids a new ctx every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contestId, allowed, realVenues, ticket, contest, onPick, isWide, inspect, hover, shown?.focus, marketLink],
  );

  /* ------------------------------------------------- deep link + focus */

  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [focus, setFocus] = useState<(DeepTarget & { n: number }) | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);

  const goTo = useCallback(
    (t: DeepTarget, opts: { stat?: string | null } = {}) => {
      setSearch("");
      setParam({ cat: t.category, q: null, stat: opts.stat ?? null, market: null, pos: null });
      setFocus((f) => ({ ...t, n: (f?.n ?? 0) + 1 }));
    },
    [setParam],
  );

  // ?market= / ?pos= → select, scroll, highlight. Applied once the markets are in.
  const marketParam = params.get("market");
  const posParam = params.get("pos");
  const ready = !!model && liquidityQ.isSuccess && marketsQ.isSuccess;
  useEffect(() => {
    if (!ready || !model || (!marketParam && !posParam)) return;
    const t = findTarget(model.cards, { market: marketParam, pos: posParam });
    // A link to a player stat (no position) also filters to that stat.
    if (t) goTo(t, { stat: t.category === "props" && !posParam ? marketParam : null });
    else setParam({ market: null, pos: null });
  }, [ready, model, marketParam, posParam, goTo, setParam]);

  useEffect(() => {
    if (!focus || !scrollEl) return;
    let tries = 0;
    let raf = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const run = () => {
      const el = scrollEl.querySelector(`[data-card="${CSS.escape(focus.cardKey)}"]`);
      if (!el) {
        if (tries++ < 10) raf = requestAnimationFrame(run);
        return;
      }
      el.scrollIntoView({ block: "start", behavior: prefersReducedMotion() ? "auto" : "smooth" });
      setHighlight(focus.cardKey);
      timer = setTimeout(() => setHighlight(null), HIGHLIGHT_MS);
    };
    raf = requestAnimationFrame(run);
    return () => {
      cancelAnimationFrame(raf);
      if (timer) clearTimeout(timer);
    };
  }, [focus, scrollEl]);

  /* ----------------------------------------------------- signals chip */

  const signals = useMemo(() => {
    const now = Date.now();
    return (signalsQ.data ?? [])
      .filter((s) => s.position?.router_contest_id === contestId)
      .filter((s) => !s.expires_at || Date.parse(s.expires_at) > now)
      .sort((a, b) => (b.strength ?? 0) - (a.strength ?? 0));
  }, [signalsQ.data, contestId]);
  const bestEdge = useMemo(() => scanBestEdge(positions), [positions]);
  const chip: HeroChip | null = useMemo(() => {
    if (!model || (signals.length === 0 && !bestEdge)) return null;
    const parts: string[] = [];
    if (signals.length) parts.push(`${signals.length} signal${signals.length === 1 ? "" : "s"}`);
    if (bestEdge) {
      const c = bestEdge.edge * 100;
      const edgePos = positions.find((p) => p.position_hash === bestEdge.positionHash);
      const who = edgePos ? sideColumnLabel(edgePos, (edgePos.side_key ?? "").toLowerCase()) : (bestEdge.participantName ?? "");
      parts.push(`${signals.length ? "best edge" : "Best edge"} +${c < 1 ? c.toFixed(1) : Math.round(c)}¢${who ? ` on ${who}` : ""}`);
    }
    const target =
      (bestEdge && findTarget(model.cards, { pos: bestEdge.positionHash, market: bestEdge.marketKey })) ||
      (signals[0] &&
        findTarget(model.cards, { pos: signals[0].position?.position_hash, market: signals[0].position?.market_key })) ||
      null;
    return { text: parts.join(" · "), onClick: () => target && goTo(target) };
  }, [model, signals, bestEdge, goTo, positions]);

  /* ------------------------------------------------------- freshness */

  const wsAge = useLiquidityAge(contestId);
  const { display } = useRelayStatus();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 5_000);
    return () => clearInterval(t);
  }, []);
  const restAge = liquidityQ.dataUpdatedAt ? Math.max(0, Math.round((Date.now() - liquidityQ.dataUpdatedAt) / 1000)) : null;
  void tick;
  const ageSec = wsAge != null && restAge != null ? Math.min(wsAge, restAge) : (wsAge ?? restAge);

  /* ------------------------------------------------- hero collapse */

  const heroRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    const el = heroRef.current;
    if (!el || !scrollEl || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setCollapsed(!e.isIntersecting), { root: scrollEl, threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, [scrollEl, contestQ.isLoading]);

  // The insight column sticks under the category bar and scrolls on its own when it's taller
  // than what's left of the viewport.
  const [stickyEl, setStickyEl] = useState<HTMLDivElement | null>(null);
  const [stick, setStick] = useState({ top: 0, max: 0 });
  useEffect(() => {
    if (!scrollEl || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const top = (stickyEl?.offsetHeight ?? 0) + 12;
      setStick((s) => (s.top === top && s.max === scrollEl.clientHeight - top - 12 ? s : { top, max: scrollEl.clientHeight - top - 12 }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(scrollEl);
    if (stickyEl) ro.observe(stickyEl);
    return () => ro.disconnect();
  }, [scrollEl, stickyEl]);

  /* ------------------------------------------------------------ view */

  const loading = contestQ.isLoading || liquidityQ.isLoading || marketsQ.isLoading;
  const loadError = liquidityQ.isError ? liquidityQ.error : marketsQ.isError ? marketsQ.error : contestQ.isError ? contestQ.error : null;
  const retry = () => {
    void contestQ.refetch();
    void liquidityQ.refetch();
    void marketsQ.refetch();
  };

  const counts = useMemo(() => {
    const src = model ? (needle ? searchCards(model.cards, needle) : model.cards) : [];
    const n: Record<Category, number> = { main: 0, spreads: 0, totals: 0, props: 0, other: 0 };
    for (const c of src) n[c.category]++;
    return n;
  }, [model, needle]);
  const totalMarkets = model?.cards.length ?? 0;

  /** The cards on screen, grouped: one group normally; one per category while searching. */
  const groups: { category: Category; cards: MarketCard[] }[] = useMemo(() => {
    if (!model) return [];
    if (needle) {
      const hits = searchCards(model.cards, needle);
      return CATEGORIES.map((c) => ({ category: c.key, cards: hits.filter((h) => h.category === c.key) })).filter(
        (g) => g.cards.length > 0,
      );
    }
    const list = model.byCategory[category]
      .map((c) => (category === "props" ? withStat(c, stat) : c))
      .filter((c): c is MarketCard => !!c);
    return [{ category, cards: list }];
  }, [model, needle, category, stat]);

  const pills = CATEGORIES.filter((c) => counts[c.key] > 0);
  // ≥1280px and no ticket open (the drawer takes the column's place).
  const showInsight = isWide && !ticket && !loadError && !!model && totalMarkets > 0 && !!shown;
  const empty = !loading && !loadError && model && totalMarkets === 0;

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1">
        <div ref={setScrollEl} className="@container min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
          {/* back link + hero */}
          <div className="px-4 pb-3 pt-3">
            <Link to="/markets" className="inline-flex min-h-11 items-center text-[13px] text-dim hover:text-amber lg:min-h-8">
              ← Markets
            </Link>
            <div ref={heroRef} className="mt-1">
              {contestQ.isLoading ? (
                <HeroSkeleton />
              ) : (
                <GameHero
                  contest={contest}
                  league={league}
                  state={hero}
                  venues={model?.venues ?? 0}
                  logos={logos}
                  freshness={{ ageSec, wsDown: display !== "connected", hasData: positions.length > 0 }}
                  chip={chip}
                />
              )}
            </div>
          </div>

          {/* sticky: the collapsed hero (mobile) + category pills (+ search on desktop) */}
          {(pills.length > 0 || loading || collapsed) && (
            <div ref={setStickyEl} className="sticky top-0 z-20 border-b border-border bg-bg/95 backdrop-blur">
              {isMobile && collapsed && <CompactHero contest={contest} state={hero} />}
              <div className="flex items-center gap-3 px-4 py-2">
                <div className="-mx-4 flex min-w-0 flex-1 items-center gap-2 overflow-x-auto px-4 lg:mx-0 lg:px-0" role="toolbar" aria-label="Market categories">
                  {loading
                    ? [0, 1, 2, 3].map((i) => <div key={i} className="skeleton h-11 w-24 shrink-0 rounded-full lg:h-8" aria-hidden />)
                    : pills.map((c) => (
                        <Pill
                          key={c.key}
                          active={!needle && category === c.key}
                          count={counts[c.key]}
                          onClick={() => {
                            if (needle) {
                              // While searching, a pill jumps to that category's results.
                              scrollEl
                                ?.querySelector(`[data-group="${c.key}"]`)
                                ?.scrollIntoView({ block: "start", behavior: prefersReducedMotion() ? "auto" : "smooth" });
                            } else selectCategory(c.key);
                          }}
                        >
                          {c.label}
                        </Pill>
                      ))}
                </div>
                {!isMobile && totalMarkets > 0 && <SearchBox value={search} onChange={setSearch} />}
              </div>
            </div>
          )}

          <div
            className={
              showInsight
                ? "grid grid-cols-[minmax(0,2fr)_minmax(340px,1fr)] items-start gap-4 px-4 pb-6 pt-3"
                : "px-4 pb-6 pt-3"
            }
          >
          <div className="flex min-w-0 flex-col gap-3">
            {isMobile && totalMarkets > 0 && <SearchBox value={search} onChange={setSearch} />}

            {loadError ? (
              isAccessError(loadError) ? (
                <AccessErrorCard error={loadError} feature="this game" compact={false} />
              ) : (
                <PlainError text="Couldn’t load this game’s markets." onRetry={retry} />
              )
            ) : loading || !model ? (
              <CardsSkeleton />
            ) : empty ? (
              <PlainNote text={isGame(contest ?? { id: "" }) ? "No markets on this game yet." : "No markets on this contest yet."} />
            ) : needle && groups.length === 0 ? (
              <PlainNote
                text={`No markets match “${needle}”`}
                action={{ label: "Clear", onClick: () => setSearch("") }}
              />
            ) : (
              <>
                {!needle && category === "props" && model.stats.length > 1 && model.byCategory.props.length > 0 && (
                  <div className="-mx-4 flex items-center gap-2 overflow-x-auto px-4" role="toolbar" aria-label="Stats">
                    <Pill active={!stat} onClick={() => setParam({ stat: null })}>
                      All
                    </Pill>
                    {model.stats.map((s) => (
                      <Pill key={s.key} active={stat === s.key} count={s.count} onClick={() => setParam({ stat: s.key })}>
                        {s.label}
                      </Pill>
                    ))}
                  </div>
                )}
                {groups.map((g) => {
                  // Main on a head-to-head game: the Game lines table + Popular props, then any
                  // other main market as a card.
                  const lines = !needle && g.category === "main" ? gl : null;
                  const cards = lines ? g.cards.filter((c) => !lines.cardKeys.includes(c.key)) : g.cards;
                  return (
                  <section key={g.category} data-group={g.category} className="flex scroll-mt-28 flex-col gap-3">
                    {needle && <SectionHeading title={categoryLabel(g.category)} count={g.cards.length} className="!pt-1 pb-2" />}
                    {lines && (
                      <>
                        <GameLinesCard gl={lines} ctx={ctx} highlight={highlight === lines.moneyline.cardKey} />
                        <PopularPropsCard props={popular} ctx={ctx} onAll={() => selectCategory("props")} />
                      </>
                    )}
                    {g.cards.length === 0 ? (
                      <PlainNote text={CATEGORIES.find((c) => c.key === g.category)?.empty ?? "Nothing here yet"} />
                    ) : cards.length === 0 ? null : (
                      <div className={CARD_GRID}>
                        {cards.map((c) => (
                          <MarketCardView
                            key={c.key}
                            card={c}
                            ctx={ctx}
                            focus={focus && focus.cardKey === c.key ? (focus as CardFocus) : null}
                            highlight={highlight === c.key}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                  );
                })}
              </>
            )}
          </div>
          {showInsight && shown && (
            <aside
              className="sticky min-w-0 self-start overflow-y-auto overscroll-contain"
              style={{ top: stick.top, maxHeight: stick.max > 200 ? stick.max : undefined }}
            >
              <InsightColumn
                key={shown.hashes.join("|")}
                title={shown.title}
                sides={shown.sides}
                focus={shown.focus}
                ctx={ctx}
                onFocus={(h) => setInsight({ title: shown.title, hashes: shown.hashes, focus: h })}
              />
            </aside>
          )}
          </div>
        </div>

        <TicketDrawer
          open={!!ticket}
          onClose={closeTicket}
          mobile={isMobile}
          header={<span className="text-[11px] font-semibold uppercase tracking-wider text-dim">Trade ticket</span>}
        >
          {ticket && (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <TradeTicket
                  key={`${ticket.position.position_hash}|${ticket.pl.partner_id}`}
                  position={ticket.position}
                  pl={ticket.pl}
                  initialMode={mode}
                  onClose={closeTicket}
                />
              </div>
              <SideBook key={ticket.position.position_hash} position={ticket.position} defaultOpen={!isMobile} />
            </div>
          )}
        </TicketDrawer>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ pieces */

function isAccessError(e: unknown): boolean {
  const code = e && typeof e === "object" && "code" in e ? String((e as { code: unknown }).code) : "";
  return (
    ["sign_in_required", "public_unavailable", "no_credential", "entitlement_required", "scope_not_granted"].includes(code)
  );
}

/** The book for the side being traded, under the ticket (collapsed by default on mobile). */
function SideBook({ position, defaultOpen }: { position: LiquidityPosition; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="shrink-0 border-t border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex min-h-11 w-full items-center gap-2 px-3 text-left text-[12px] text-dim hover:text-ink"
      >
        <span className="min-w-0 flex-1 truncate font-semibold uppercase tracking-wider">Book for this side</span>
        <span aria-hidden>{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="max-h-[38dvh] overflow-y-auto overscroll-contain">
          <DepthBook positions={[position]} />
        </div>
      )}
    </div>
  );
}

function SearchBox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative min-w-0 lg:w-64 lg:shrink-0">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-faint" aria-hidden>
        ⌕
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && value) {
            e.preventDefault();
            onChange("");
          }
        }}
        placeholder="Search markets or players"
        aria-label="Search markets"
        type="search"
        className="min-h-11 w-full rounded-full border border-border-2 bg-panel-2 py-2 pl-8 pr-3 text-[14px] text-ink outline-none placeholder:text-faint focus:border-amber lg:min-h-8 lg:py-1.5 lg:text-[13px]"
      />
    </div>
  );
}

function PlainNote({ text, action }: { text: string; action?: { label: string; onClick: () => void } }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-panel-2 p-4 text-[13px] text-dim">
      <span className="min-w-0 flex-1">{text}</span>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="min-h-11 rounded-full border border-border-2 px-4 text-[13px] text-ink hover:bg-raised lg:min-h-8"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

function PlainError({ text, onRetry }: { text: string; onRetry: () => void }) {
  return <PlainNote text={text} action={{ label: "Try again", onClick: onRetry }} />;
}

function CardsSkeleton() {
  return (
    <div className={CARD_GRID}>
      <CardSkeleton />
      <CardSkeleton />
      <CardSkeleton rows={2} />
      <CardSkeleton />
    </div>
  );
}
